import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { ArrowRight, TrendingUp, Banknote, Users } from 'lucide-react';
import { ETAPE } from './crmLabels';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('ro-RO'));

export default function ReportsView({ api }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    for (let i = 0; i < 4; i++) {
      try {
        const d = await api.get('/reports');
        if (d && d.perEtapa) { setData(d); return; }
      } catch (e) { /* retry */ }
      await new Promise((r) => setTimeout(r, 800));
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-sm text-gray-500">Se încarcă rapoartele…</p>;

  const { perEtapa, funnel, credite } = data;
  const steps = [
    { label: '1 → 2', val: funnel.rate['1_2'] },
    { label: '2 → 3', val: funnel.rate['2_3'] },
    { label: '3 → 4', val: funnel.rate['3_4'] },
  ];

  return (
    <div data-testid="crm-reports" className="space-y-4">
      {/* Pipeline per etapă */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4 text-blue-600" /> Clienți activi per etapă
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((e) => (
              <div key={e} className="border rounded-xl p-4 text-center" data-testid={`crm-report-etapa-${e}`}>
                <div className="text-3xl font-bold text-blue-700">{perEtapa[e] ?? 0}</div>
                <div className="text-xs text-gray-500 mt-1">{ETAPE[e]}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Funnel / conversie */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-600" /> Rata de conversie între etape
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            {[1, 2, 3, 4].map((k, i) => (
              <React.Fragment key={k}>
                <div className="text-center">
                  <div className="text-xl font-bold">{funnel.reached[k] ?? 0}</div>
                  <div className="text-[11px] text-gray-500">au atins et. {k}</div>
                </div>
                {i < 3 && <ArrowRight className="h-4 w-4 text-gray-400" />}
              </React.Fragment>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {steps.map((s) => (
              <div key={s.label} className="border rounded-lg p-3 text-center" data-testid={`crm-report-rate-${s.label.replace(/\s|→/g, '')}`}>
                <div className="text-2xl font-bold text-emerald-700">{s.val}%</div>
                <div className="text-xs text-gray-500">{s.label}</div>
              </div>
            ))}
            <div className="border rounded-lg p-3 text-center bg-emerald-50">
              <div className="text-2xl font-bold text-emerald-700">{funnel.rate.total}%</div>
              <div className="text-xs text-gray-500">Lead → Credit</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Credite disbursate */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Banknote className="h-4 w-4 text-purple-600" /> Credite disbursate
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-6">
            <div>
              <div className="text-3xl font-bold text-purple-700" data-testid="crm-report-total-credite">{credite.total}</div>
              <div className="text-xs text-gray-500">credite</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-700" data-testid="crm-report-valoare-totala">{fmt(credite.valoareTotala)}</div>
              <div className="text-xs text-gray-500">valoare totală (RON)</div>
            </div>
          </div>

          {credite.perBanca.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Per bancă</h4>
              <div className="space-y-1">
                {credite.perBanca.map((b) => (
                  <div key={b.banca} className="flex items-center justify-between text-sm border-b py-1">
                    <span>{b.banca} <Badge variant="secondary" className="ml-1">{b.count}</Badge></span>
                    <span className="font-medium">{fmt(b.valoare)} RON</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {credite.perLuna.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Per lună</h4>
              <div className="space-y-1">
                {credite.perLuna.map((m) => (
                  <div key={m.luna} className="flex items-center justify-between text-sm border-b py-1">
                    <span>{m.luna} <Badge variant="secondary" className="ml-1">{m.count}</Badge></span>
                    <span className="font-medium">{fmt(m.valoare)} RON</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {credite.total === 0 && <p className="text-sm text-gray-400">Niciun credit disbursat încă.</p>}
        </CardContent>
      </Card>
    </div>
  );
}
